import { create } from 'zustand'

export { useLayoutStore }

type ActiveDrawer = {
  id: string
  width: number
  onClose: () => void
  // Closed, but its panel is still animating out
  closing: boolean
}

const useLayoutStore = () => {
  const sidebarOpen = useStore((state) => state.sidebarOpen)
  const storeSidebarOpen = useStore((state) => state.storeSidebarOpen)
  const toggleSidebar = useStore((state) => state.toggleSidebar)
  const activeDrawer = useStore((state) => state.activeDrawer)
  const registerDrawer = useStore((state) => state.registerDrawer)
  const unregisterDrawer = useStore((state) => state.unregisterDrawer)
  const removeDrawer = useStore((state) => state.removeDrawer)

  return {
    sidebarOpen,
    storeSidebarOpen,
    toggleSidebar,
    activeDrawer,
    registerDrawer,
    unregisterDrawer,
    removeDrawer,
  }
}

const useStore = create<{
  // Whether the sidebar is open, next to the page or over it. Null until the
  // layout settles it on its first render.
  sidebarOpen: boolean | null
  storeSidebarOpen: (open: boolean) => void
  toggleSidebar: () => void
  // The side drawer that is open or closing, so the layout can check whether
  // it fits next to the page, and keeps it that way while its panel animates
  // out. Only one drawer is open at a time: opening another one closes the
  // previous drawer.
  activeDrawer: ActiveDrawer | null
  registerDrawer: (drawer: Omit<ActiveDrawer, 'closing'>) => void
  // Marks the drawer as closing, until its panel has closed
  unregisterDrawer: (id: string) => void
  // Removes the drawer once its panel has closed
  removeDrawer: (id: string) => void
}>((set, get) => ({
  sidebarOpen: null,
  storeSidebarOpen: (sidebarOpen) => set(() => ({ sidebarOpen })),
  toggleSidebar: () => get().storeSidebarOpen(!get().sidebarOpen),
  activeDrawer: null,
  registerDrawer: (drawer) => {
    const previous = get().activeDrawer

    set(() => ({ activeDrawer: { ...drawer, closing: false } }))

    if (previous && previous.id !== drawer.id && !previous.closing) {
      previous.onClose()
    }
  },
  unregisterDrawer: (id) =>
    set((state) =>
      state.activeDrawer?.id === id
        ? { activeDrawer: { ...state.activeDrawer, closing: true } }
        : state,
    ),
  removeDrawer: (id) =>
    set((state) =>
      state.activeDrawer?.id === id && state.activeDrawer.closing
        ? { activeDrawer: null }
        : state,
    ),
}))
